/**
 * User Details Dialog Component
 *
 * A modal dialog for editing user details.
 * All fields are editable for admin users.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
import { OrganizationUser } from '@/types/user-management.types';
import { UserRole } from '@/types/enums/UserRole';
import { IconName } from '@/icons';
import { Avatar } from '@/components/ui/avatar';

export interface UserDetailsDialogProps {
  /** The user to display */
  user: OrganizationUser | null;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Loading state during user update */
  isLoading?: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when save is clicked with updated data */
  onSave: (updatedData: Partial<OrganizationUser>) => Promise<void>;
}

/**
 * User Details Dialog
 *
 * Displays user information in edit mode for admins.
 */
export function UserDetailsDialog({
  user,
  isOpen,
  isLoading = false,
  onClose,
  onSave,
}: UserDetailsDialogProps) {
  const { openModal, closeModal } = useModalManager();
  const [editedUser, setEditedUser] = useState<Partial<OrganizationUser>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const isUsernameValid = /^[a-zA-Z0-9]{2,50}$/.test(
    (editedUser.username || '').trim()
  );

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.USER_DETAILS);
    } else {
      closeModal(ModalId.USER_DETAILS);
    }
  }, [isOpen, openModal, closeModal]);

  // Reset edit state when user changes or dialog opens
  useEffect(() => {
    if (user) {
      setEditedUser({
        username: user.username,
        display_name: user.display_name,
        email: user.email,
        role: user.role,
        profile_image: user.profile_image,
      });
      setHasChanges(false);
    }
  }, [user, isOpen]);

  if (!user) return null;

  const handleSave = async () => {
    await onSave(editedUser);
    setHasChanges(false);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleChange = (field: string, value: any) => {
    setEditedUser({ ...editedUser, [field]: value });
    setHasChanges(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Profile Picture */}
          <div className="space-y-2">
            <Label>Profile Picture</Label>
            <div className="flex items-center gap-3">
              <Avatar
                src={editedUser.profile_image}
                displayName={editedUser.display_name || user.display_name}
                userId={user.id}
                size={56}
              />
              {editedUser.profile_image && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleChange('profile_image', '')}
                >
                  Remove Picture
                </Button>
              )}
            </div>
          </div>

          {/* Username (Editable) */}
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={editedUser.username || ''}
              onChange={e => handleChange('username', e.target.value)}
              placeholder="Enter username"
              maxLength={50}
            />
            <p
              className={`text-xs ${!isUsernameValid ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {!isUsernameValid
                ? 'Username must be 2–50 alphanumeric characters (a–z, A–Z, 0–9)'
                : '2–50 alphanumeric characters, must be unique'}
            </p>
          </div>

          {/* Display Name (Editable) */}
          <div className="space-y-2">
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              value={editedUser.display_name || ''}
              onChange={e => handleChange('display_name', e.target.value)}
              placeholder="Enter display name"
            />
          </div>

          {/* Email (Editable) */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={editedUser.email || ''}
              onChange={e => handleChange('email', e.target.value || null)}
              placeholder="user@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty if user doesn't have an email address
            </p>
          </div>

          {/* Role (Editable) */}
          <div className="space-y-2">
            <select
              id="role"
              value={editedUser.role || user.role}
              onChange={e => handleChange('role', e.target.value as UserRole)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={UserRole.ADMIN}>Admin</option>
              <option value={UserRole.MENTOR}>Mentor</option>
              <option value={UserRole.STUDENT}>Student</option>
            </select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <AsyncButton
            onClick={handleSave}
            disabled={!hasChanges || !isUsernameValid}
            isLoading={isLoading}
            icon={IconName.SAVE}
            loadingText="Saving..."
          >
            Save Changes
          </AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
